import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Mail, Phone, ChevronRight, Send, Loader2, CheckCircle2,
  Briefcase, Globe2, ShieldCheck, Lightbulb, MapPinned, Repeat,
  Users, BedDouble, Tent, Mountain, Car, UtensilsCrossed, Building2, Network,
  Sparkles, Handshake, Plane, Building, Calendar, FileText,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import PageStickyNav from "@/components/PageStickyNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1517825738774-7de9363ef735?auto=format&fit=crop&w=2400&q=80";
const ABOUT_IMAGE =
  "https://images.unsplash.com/photo-1551632811-561732d1e306?auto=format&fit=crop&w=1800&q=80";
const NETWORK_IMAGE =
  "https://images.unsplash.com/photo-1542401886-65d6c61db217?auto=format&fit=crop&w=1800&q=80";

// ── COPY ──────────────────────────────────────────────────────────────
const COPY = {
  // SEO / hero
  hero_overline: {
    en: "Partner programme · B2B",
    fr: "Programme partenaires · B2B",
    es: "Programa partners · B2B",
  },
  hero_title: {
    en: "Your trusted hospitality partner",
    fr: "Votre partenaire hospitalier",
    es: "Su partner hostelero de confianza",
  },
  hero_title_accent: {
    en: "in Southern Morocco.",
    fr: "de confiance dans le sud du Maroc.",
    es: "en el sur de Marruecos.",
  },
  hero_sub: {
    en: "Travel agencies, tour operators, DMCs, MICE planners and event producers — design unforgettable Moroccan journeys with the infrastructure, expertise and warmth of a family group that has shaped the south for more than thirty years.",
    fr: "Agences de voyages, tour-opérateurs, DMC, MICE et organisateurs d'événements — composez des voyages marocains inoubliables avec l'infrastructure, l'expertise et la chaleur d'un groupe familial qui façonne le sud depuis plus de trente ans.",
    es: "Agencias de viajes, turoperadores, DMC, MICE y organizadores de eventos — diseñe viajes marroquíes inolvidables con la infraestructura, la experiencia y la calidez de un grupo familiar que esculpe el sur desde hace más de treinta años.",
  },
  hero_cta_primary: { en: "Become a partner", fr: "Devenir partenaire", es: "Ser partner" },
  hero_cta_secondary: { en: "Request a proposal", fr: "Demander une proposition", es: "Solicitar una propuesta" },
  hero_cta_tertiary: { en: "Contact our B2B team", fr: "Contacter l'équipe B2B", es: "Contactar al equipo B2B" },

  // Stats strip
  stats: [
    { value: "30+", label: { en: "Years operating", fr: "Années d'activité", es: "Años de actividad" } },
    { value: "9", label: { en: "Houses across the south", fr: "Adresses dans le sud", es: "Direcciones en el sur" } },
    { value: "120K+", label: { en: "Guests welcomed each year", fr: "Hôtes accueillis par an", es: "Huéspedes recibidos al año" } },
    { value: "1,000+", label: { en: "Largest desert event hosted", fr: "Plus grand événement désert", es: "Mayor evento desierto" } },
  ],

  // About section
  about_overline: { en: "About Grup Xaluca", fr: "À propos de Grup Xaluca", es: "Sobre Grup Xaluca" },
  about_title: {
    en: "Three decades of Moroccan hospitality, built family by family.",
    fr: "Trois décennies d'hospitalité marocaine, bâties famille après famille.",
    es: "Tres décadas de hospitalidad marroquí, construidas familia a familia.",
  },
  about_lead: {
    en: "Grup Xaluca is a Moroccan-owned, family-run hospitality group founded in Arfoud. Over thirty years we have grown from a single kasbah at the gate of the Sahara into a regional operator of hotels, riads, mountain refuges, desert camps, restaurants, transportation fleets and large-scale event infrastructure — all owned, all operated by the same family-led teams.",
    fr: "Grup Xaluca est un groupe hôtelier marocain, familial et indépendant, fondé à Arfoud. En plus de trente ans, nous sommes passés d'une kasbah à la porte du Sahara à un opérateur régional d'hôtels, riads, refuges de montagne, camps du désert, restaurants, flottes de transport et infrastructures événementielles de grande envergure — entièrement détenus et opérés par les mêmes équipes familiales.",
    es: "Grup Xaluca es un grupo hostelero marroquí, familiar e independiente, fundado en Arfoud. En más de treinta años hemos pasado de una kasbah a las puertas del Sáhara a un operador regional de hoteles, riads, refugios de montaña, campamentos del desierto, restaurantes, flotas de transporte e infraestructura para eventos a gran escala — todo propio, todo operado por los mismos equipos familiares.",
  },
  about_p2: {
    en: "From private moonlit dinners on the dunes to multi-property programmes for thousand-strong incentive groups, we operate the south end-to-end. One contact at the office in Arfoud. One unified team on the ground. One signature warmth woven into every transfer, every welcome, every wake-up.",
    fr: "Du dîner privé sous la lune dans les dunes aux programmes multi-adresses pour des incentives de mille personnes, nous opérons le sud de bout en bout. Un interlocuteur unique au bureau d'Arfoud. Une seule équipe sur le terrain. Une chaleur signature tissée dans chaque transfert, chaque accueil, chaque réveil.",
    es: "Desde cenas privadas a la luz de la luna en las dunas hasta programas multi-propiedad para grupos incentivos de mil personas, operamos el sur de extremo a extremo. Un interlocutor único en la oficina de Arfoud. Un solo equipo en el terreno. Una calidez característica tejida en cada traslado, cada bienvenida, cada despertar.",
  },
  about_facts: [
    { en: "Family-owned, Moroccan-led group", fr: "Groupe familial, dirigé par des Marocains", es: "Grupo familiar, dirigido por marroquíes" },
    { en: "30+ years of hospitality experience", fr: "30+ années d'expérience hôtelière", es: "30+ años de experiencia hostelera" },
    { en: "120,000+ travellers hosted each year", fr: "120 000+ voyageurs accueillis par an", es: "120.000+ viajeros recibidos al año" },
    { en: "One unified team — Arfoud HQ", fr: "Une équipe unifiée — siège à Arfoud", es: "Un equipo unificado — sede en Arfoud" },
  ],

  // Why partner
  why_overline: { en: "Why partner with Grup Xaluca", fr: "Pourquoi devenir partenaire", es: "Por qué asociarse" },
  why_title: {
    en: "Eight pillars that protect your bookings and your reputation.",
    fr: "Huit piliers qui protègent vos réservations et votre réputation.",
    es: "Ocho pilares que protegen sus reservas y su reputación.",
  },
  why_lead: {
    en: "Travel professionals stay with Grup Xaluca for one reason: the south of Morocco runs more quietly when it runs through us. Below is what that means in practice.",
    fr: "Les professionnels du voyage restent avec Grup Xaluca pour une raison : le sud du Maroc tourne plus paisiblement lorsqu'il passe par nous. Voici ce que cela signifie concrètement.",
    es: "Los profesionales del viaje se quedan con Grup Xaluca por una razón: el sur de Marruecos funciona con más calma cuando pasa por nosotros. Esto es lo que significa en la práctica.",
  },

  // Network section
  network_overline: { en: "Our hospitality network", fr: "Notre réseau hôtelier", es: "Nuestra red hostelera" },
  network_title: {
    en: "One destination. One operator. End-to-end.",
    fr: "Une destination. Un opérateur. De bout en bout.",
    es: "Un destino. Un operador. De extremo a extremo.",
  },
  network_lead: {
    en: "Every link in the chain — beds, vehicles, kitchens, dunes, events — sits under the same roof. That is rare in Morocco. It is the reason our agency partners sleep at night.",
    fr: "Chaque maillon de la chaîne — lits, véhicules, cuisines, dunes, événements — appartient à la même maison. C'est rare au Maroc. C'est pourquoi nos agences partenaires dorment tranquilles.",
    es: "Cada eslabón de la cadena — camas, vehículos, cocinas, dunas, eventos — está bajo el mismo techo. Es raro en Marruecos. Es la razón por la que nuestras agencias partners duermen tranquilas.",
  },

  // Solutions
  solutions_overline: { en: "Solutions for travel professionals", fr: "Solutions pour les professionnels", es: "Soluciones para profesionales" },
  solutions_title: {
    en: "Four programmes, one signature.",
    fr: "Quatre programmes, une signature.",
    es: "Cuatro programas, una firma.",
  },
  solutions_lead: {
    en: "Whether you're booking a single suite for a couple or chartering a 1,000-pax dinner under the stars, our teams shape the offer around your brand — not the other way around.",
    fr: "Que vous réserviez une suite pour un couple ou que vous affrétiez un dîner pour mille convives sous les étoiles, nos équipes façonnent l'offre autour de votre marque — et non l'inverse.",
    es: "Tanto si reserva una suite para una pareja como si organiza una cena para 1.000 personas bajo las estrellas, nuestros equipos modelan la oferta en torno a su marca — no al revés.",
  },

  // Trust
  trust_overline: { en: "Trusted by travel professionals worldwide", fr: "Approuvé par les professionnels du voyage", es: "Confianza de profesionales del viaje" },
  trust_title: {
    en: "Built on conversations that last a decade or more.",
    fr: "Construit sur des conversations qui durent une décennie ou plus.",
    es: "Construido sobre conversaciones que duran una década o más.",
  },

  // Contact
  contact_overline: { en: "B2B contact", fr: "Contact B2B", es: "Contacto B2B" },
  contact_title: {
    en: "Let's build extraordinary Moroccan experiences together.",
    fr: "Bâtissons ensemble des expériences marocaines extraordinaires.",
    es: "Construyamos juntos experiencias marroquíes extraordinarias.",
  },
  contact_lead: {
    en: "Tell us about your clients, your dates and your ambition. Within twenty-four hours we send back a tailored proposal, full availability and a single point of contact who will stay with the file until the last farewell at the gate.",
    fr: "Parlez-nous de vos clients, de vos dates et de votre ambition. Sous vingt-quatre heures, nous vous renvoyons une proposition sur mesure, les disponibilités complètes et un interlocuteur unique qui suivra le dossier jusqu'au dernier adieu sur le seuil.",
    es: "Háblenos de sus clientes, sus fechas y su ambición. En menos de veinticuatro horas le devolvemos una propuesta a medida, disponibilidad completa y un único interlocutor que acompañará el dossier hasta la última despedida en la puerta.",
  },

  // Form labels
  form_company: { en: "Company / agency name", fr: "Société / agence", es: "Empresa / agencia" },
  form_contact: { en: "Your full name", fr: "Votre nom complet", es: "Su nombre completo" },
  form_role: { en: "Role / position", fr: "Fonction", es: "Cargo" },
  form_email: { en: "Professional email", fr: "Email professionnel", es: "Correo profesional" },
  form_phone: { en: "Phone / WhatsApp", fr: "Téléphone / WhatsApp", es: "Teléfono / WhatsApp" },
  form_country: { en: "Country / source market", fr: "Pays / marché source", es: "País / mercado emisor" },
  form_type: { en: "Type of business", fr: "Type d'activité", es: "Tipo de actividad" },
  form_timing: { en: "Expected travel window", fr: "Période de voyage envisagée", es: "Periodo de viaje previsto" },
  form_volume: { en: "Estimated annual volume", fr: "Volume annuel estimé", es: "Volumen anual estimado" },
  form_message: { en: "Tell us about your project", fr: "Parlez-nous de votre projet", es: "Cuéntenos su proyecto" },
  form_message_ph: {
    en: "Programme, group size, properties or experiences in mind, on-the-ground requirements…",
    fr: "Programme, taille du groupe, adresses ou expériences envisagées, besoins sur place…",
    es: "Programa, tamaño del grupo, adresses o experiencias en mente, necesidades sobre el terreno…",
  },
  form_submit: { en: "Send to the B2B team", fr: "Envoyer à l'équipe B2B", es: "Enviar al equipo B2B" },
  form_sending: { en: "Sending…", fr: "Envoi en cours…", es: "Enviando…" },
  form_success: {
    en: "Your message reached the B2B team. We will reply within 24 hours.",
    fr: "Votre message est bien arrivé à l'équipe B2B. Réponse sous 24 heures.",
    es: "Su mensaje llegó al equipo B2B. Responderemos en menos de 24 horas.",
  },
  form_error: {
    en: "Something went wrong. Please try again or write to partners@xaluca.com.",
    fr: "Une erreur est survenue. Réessayez ou écrivez à partners@xaluca.com.",
    es: "Algo salió mal. Inténtelo de nuevo o escriba a partners@xaluca.com.",
  },
  form_footnote: {
    en: "Or write directly · partners@xaluca.com · +212 535 57 60 22",
    fr: "Ou écrivez directement · partners@xaluca.com · +212 535 57 60 22",
    es: "O escriba directamente · partners@xaluca.com · +212 535 57 60 22",
  },
};

// ── DATA ──────────────────────────────────────────────────────────────
const PILLARS = [
  {
    icon: Briefcase,
    title: { en: "Experience", fr: "Expérience", es: "Experiencia" },
    body: {
      en: "More than 30 years operating across hospitality, destination management and event production in the south of Morocco. We have welcomed every kind of traveller — and learnt from each.",
      fr: "Plus de 30 ans d'opérations en hôtellerie, gestion de destination et production événementielle dans le sud du Maroc. Nous avons accueilli toutes sortes de voyageurs — et appris de chacun.",
      es: "Más de 30 años operando en hostelería, gestión de destinos y producción de eventos en el sur de Marruecos. Hemos recibido a todo tipo de viajeros — y aprendido de cada uno.",
    },
  },
  {
    icon: ShieldCheck,
    title: { en: "Reliability", fr: "Fiabilité", es: "Fiabilidad" },
    body: {
      en: "Owned fleet, owned camps, owned kitchens. When something needs solving, it's solved by our own team — not by a sub-contractor we've never met.",
      fr: "Flotte propre, camps propres, cuisines propres. Quand un imprévu survient, ce sont nos équipes qui le résolvent — jamais un sous-traitant inconnu.",
      es: "Flota propia, campamentos propios, cocinas propias. Cuando hay que resolver algo, lo resuelven nuestros equipos — no un subcontratista al que nunca hemos visto.",
    },
  },
  {
    icon: MapPinned,
    title: { en: "Local knowledge", fr: "Connaissance locale", es: "Conocimiento local" },
    body: {
      en: "Roads, oases, ksour, festivals, market days, military checkpoints — we know the south because we live in it. We share that map with every partner.",
      fr: "Routes, oasis, ksour, festivals, jours de souk, points de contrôle — nous connaissons le sud parce que nous y vivons. Nous partageons cette carte avec chaque partenaire.",
      es: "Carreteras, oasis, ksour, festivales, días de zoco, controles militares — conocemos el sur porque vivimos en él. Compartimos ese mapa con cada partner.",
    },
  },
  {
    icon: Sparkles,
    title: { en: "Hospitality excellence", fr: "Excellence hospitalière", es: "Excelencia hostelera" },
    body: {
      en: "Mint tea at every gate, fresh bread at every breakfast, a host who knows the guest's name. Quietly excellent — never theatrical.",
      fr: "Thé à la menthe à chaque porte, pain frais à chaque petit-déjeuner, un hôte qui connaît le nom du client. Discrètement excellent — jamais théâtral.",
      es: "Té con menta en cada puerta, pan recién hecho en cada desayuno, un anfitrión que conoce el nombre del huésped. Discretamente excelente — nunca teatral.",
    },
  },
  {
    icon: Network,
    title: { en: "Infrastructure", fr: "Infrastructure", es: "Infraestructura" },
    body: {
      en: "Nine houses · 600+ keys · 80+ vehicles · custom desert camps from 6 to 1,000 guests · a private event ground at Erg Chebbi · two restaurant kitchens trained for plated dinners on dunes.",
      fr: "Neuf adresses · 600+ clés · 80+ véhicules · camps désert sur mesure de 6 à 1 000 convives · une scène événementielle privée à l'Erg Chebbi · deux cuisines formées au dîner servi à l'assiette dans les dunes.",
      es: "Nueve direcciones · 600+ llaves · 80+ vehículos · campamentos a medida de 6 a 1.000 personas · un escenario privado en Erg Chebbi · dos cocinas formadas para cenas emplatadas en las dunas.",
    },
  },
  {
    icon: Repeat,
    title: { en: "Flexibility", fr: "Flexibilité", es: "Flexibilidad" },
    body: {
      en: "Tailor-made by default. FITs, families, fashion shoots, board retreats, incentive programmes, expedition logistics — every brief is built from a blank page in conversation with you.",
      fr: "Sur mesure par défaut. FIT, familles, shootings mode, séminaires direction, programmes incentives, logistique d'expédition — chaque brief est conçu à partir d'une page blanche, en conversation avec vous.",
      es: "A medida por defecto. FIT, familias, sesiones de moda, retiros de dirección, programas incentivos, logística de expedición — cada brief se construye desde una hoja en blanco, en conversación con usted.",
    },
  },
  {
    icon: Lightbulb,
    title: { en: "Innovation", fr: "Innovation", es: "Innovación" },
    body: {
      en: "Continuous investment in new houses, sustainable energy, water-positive farming, in-house experience design and a private API that lets booking partners hook into live availability.",
      fr: "Investissement continu dans de nouvelles adresses, énergie durable, agriculture positive en eau, design d'expérience interne et une API privée qui connecte les partenaires aux disponibilités en temps réel.",
      es: "Inversión continua en nuevas direcciones, energía sostenible, agricultura positiva en agua, diseño de experiencias interno y una API privada que conecta a los partners con la disponibilidad en tiempo real.",
    },
  },
  {
    icon: Handshake,
    title: { en: "Long-term partnership", fr: "Partenariat durable", es: "Partnership a largo plazo" },
    body: {
      en: "Three of our top-five agency partners have worked with the group for more than 15 years. Transparent commercial terms, allocations that grow with you, and an open door at the office in Arfoud.",
      fr: "Trois de nos cinq plus grandes agences partenaires travaillent avec le groupe depuis plus de 15 ans. Conditions commerciales transparentes, allotements qui grandissent avec vous, et une porte ouverte au bureau d'Arfoud.",
      es: "Tres de nuestros cinco principales partners agencia llevan más de 15 años con el grupo. Condiciones comerciales transparentes, allotments que crecen con usted, y una puerta abierta en la oficina de Arfoud.",
    },
  },
];

const NETWORK = [
  {
    icon: BedDouble,
    title: { en: "Hotels & kasbahs", fr: "Hôtels & kasbahs", es: "Hoteles y kasbahs" },
    stat: { en: "9 houses · 600+ keys", fr: "9 adresses · 600+ clés", es: "9 casas · 600+ llaves" },
    body: {
      en: "From the Atlas to the dunes — kasbahs, riads, an auberge and a mountain refuge, each with its own kitchen and signature.",
      fr: "De l'Atlas aux dunes — kasbahs, riads, une auberge et un refuge de montagne, chacun avec sa cuisine et sa signature.",
      es: "Del Atlas a las dunas — kasbahs, riads, una auberge y un refugio de montaña, cada uno con su cocina y su firma.",
    },
  },
  {
    icon: Tent,
    title: { en: "Desert camps & bivouacs", fr: "Camps & bivouacs", es: "Campamentos y vivacs" },
    stat: { en: "3 tiers · 6 to 1,000 guests", fr: "3 niveaux · 6 à 1 000 convives", es: "3 niveles · 6 a 1.000 personas" },
    body: {
      en: "Auberge tents, luxury suites and bespoke tented villages erected for incentives, weddings and film productions across Erg Chebbi.",
      fr: "Tentes d'auberge, suites de luxe et villages de tentes sur mesure érigés pour incentives, mariages et productions cinéma à travers l'Erg Chebbi.",
      es: "Tiendas de auberge, suites de lujo y aldeas a medida levantadas para incentivos, bodas y producciones de cine en todo Erg Chebbi.",
    },
  },
  {
    icon: Mountain,
    title: { en: "Mountain lodges", fr: "Refuges de montagne", es: "Refugios de montaña" },
    stat: { en: "M'Goun massif · 2,200 m", fr: "Massif du M'Goun · 2 200 m", es: "Macizo del M'Goun · 2.200 m" },
    body: {
      en: "Trekking, ski-mountaineering, summit ascents and full-board logistics for adventure programmes in the central High Atlas.",
      fr: "Trekking, ski-alpinisme, ascensions et pension complète pour programmes aventure dans le Haut Atlas central.",
      es: "Trekking, esquí de montaña, ascensos y pensión completa para programas de aventura en el Alto Atlas central.",
    },
  },
  {
    icon: UtensilsCrossed,
    title: { en: "Restaurants & private dining", fr: "Restaurants & repas privés", es: "Restaurantes y cenas privadas" },
    stat: { en: "12 outlets · plated dinners on dunes", fr: "12 adresses · dîners servis dans les dunes", es: "12 puntos · cenas emplatadas en las dunas" },
    body: {
      en: "From the cellar restaurant at Kasbah Arfoud to plated dinners under the Milky Way — kitchens trained in modern Moroccan and international cuisine.",
      fr: "De la cave-restaurant de la Kasbah d'Arfoud au dîner servi à l'assiette sous la Voie lactée — cuisines formées à la cuisine marocaine moderne et internationale.",
      es: "Del restaurante-bodega de la Kasbah d'Arfoud a cenas emplatadas bajo la Vía Láctea — cocinas formadas en cocina marroquí moderna e internacional.",
    },
  },
  {
    icon: Building2,
    title: { en: "Event venues", fr: "Espaces événementiels", es: "Espacios para eventos" },
    stat: { en: "Indoor · outdoor · open desert", fr: "Intérieur · extérieur · plein désert", es: "Interior · exterior · pleno desierto" },
    body: {
      en: "Conference rooms, courtyards, riad terraces and a private dune ground at Erg Chebbi designed for up to 1,000 guests under a single canvas.",
      fr: "Salles de conférence, patios, terrasses de riad et une scène privée à l'Erg Chebbi conçue pour jusqu'à 1 000 convives sous une seule toile.",
      es: "Salas de conferencias, patios, terrazas de riad y un escenario privado en Erg Chebbi para hasta 1.000 personas bajo un mismo lienzo.",
    },
  },
  {
    icon: Car,
    title: { en: "Transportation fleet", fr: "Flotte de transport", es: "Flota de transporte" },
    stat: { en: "80+ vehicles · 4×4 to coaches", fr: "80+ véhicules · 4×4 aux autocars", es: "80+ vehículos · 4×4 a autocares" },
    body: {
      en: "Sedans, vans, 4×4 expedition vehicles, mini-coaches and full coaches — drivers trained for guests, schedules and the dunes.",
      fr: "Berlines, vans, 4×4 d'expédition, mini-cars et grands cars — chauffeurs formés à l'accueil, aux horaires et aux dunes.",
      es: "Berlinas, furgonetas, 4×4 de expedición, minibuses y autocares — conductores formados para el huésped, el horario y las dunas.",
    },
  },
  {
    icon: Network,
    title: { en: "Operations HQ · Arfoud", fr: "QG opérations · Arfoud", es: "QG operaciones · Arfoud" },
    stat: { en: "One number, one team", fr: "Un numéro, une équipe", es: "Un número, un equipo" },
    body: {
      en: "Reservations, MICE coordination, transport dispatch and 24/7 on-call duty manager — all from a single office at the gate of the desert.",
      fr: "Réservations, coordination MICE, régulation des transports et manager de permanence 24/7 — depuis un seul bureau à la porte du désert.",
      es: "Reservas, coordinación MICE, regulación de transportes y manager de guardia 24/7 — desde una sola oficina a las puertas del desierto.",
    },
  },
  {
    icon: Globe2,
    title: { en: "Trilingual service", fr: "Service trilingue", es: "Servicio trilingüe" },
    stat: { en: "EN · FR · ES · AR", fr: "EN · FR · ES · AR", es: "EN · FR · ES · AR" },
    body: {
      en: "Every contract, every itinerary, every welcome — delivered in your client's language by native speakers on our team.",
      fr: "Chaque contrat, chaque itinéraire, chaque accueil — livré dans la langue de votre client par des locuteurs natifs de notre équipe.",
      es: "Cada contrato, cada itinerario, cada bienvenida — entregado en el idioma de su cliente por miembros nativos de nuestro equipo.",
    },
  },
];

const SOLUTIONS = [
  {
    icon: Plane,
    title: { en: "Travel agencies", fr: "Agences de voyages", es: "Agencias de viajes" },
    pitch: {
      en: "Confidential rates, instant availability and one contact who replies in the same hour. We handle accommodation, transfers and on-the-ground concierge — you keep ownership of the client.",
      fr: "Tarifs confidentiels, disponibilités instantanées et un interlocuteur qui répond dans l'heure. Hébergement, transferts, conciergerie sur place — vous conservez la relation client.",
      es: "Tarifas confidenciales, disponibilidad instantánea y un interlocutor que responde en la misma hora. Alojamiento, traslados y conserjería local — usted mantiene la relación con el cliente.",
    },
    bullets: [
      { en: "Net rates with seasonal allotments", fr: "Tarifs nets avec allotements saisonniers", es: "Tarifas netas con allotments por temporada" },
      { en: "Bilingual contracting in 24h", fr: "Contractualisation bilingue sous 24h", es: "Contratación bilingüe en 24h" },
      { en: "Per-booking concierge desk", fr: "Conciergerie dédiée par dossier", es: "Conserjería dedicada por reserva" },
    ],
  },
  {
    icon: Globe2,
    title: { en: "Tour operators & DMCs", fr: "Tour-opérateurs & DMC", es: "Turoperadores y DMC" },
    pitch: {
      en: "Multi-day itineraries from Marrakech to Merzouga, contracted allocations across our nine houses, group-leader rates, and full on-the-ground management of every transfer, lunch stop and welcome.",
      fr: "Itinéraires multi-jours de Marrakech à Merzouga, allotements contractés sur nos neuf adresses, tarifs tour-leader, et gestion complète sur place de chaque transfert, déjeuner et accueil.",
      es: "Itinerarios multi-día de Marrakech a Merzouga, allotments contratados en nuestras nueve casas, tarifas tour-leader y gestión completa sobre el terreno de cada traslado, comida y bienvenida.",
    },
    bullets: [
      { en: "Series contracts & block bookings", fr: "Contrats séries & blocks", es: "Contratos series y bloques" },
      { en: "Coach-friendly properties", fr: "Adresses adaptées aux autocars", es: "Direcciones aptas para autocares" },
      { en: "Single PNR / multi-property file", fr: "Dossier unique multi-adresses", es: "Expediente único multi-adresses" },
    ],
  },
  {
    icon: Building,
    title: { en: "MICE & events", fr: "MICE & événements", es: "MICE y eventos" },
    pitch: {
      en: "Corporate seminars, incentives, conferences, product launches and gala nights — staged across kasbahs, riads and the open desert. From AV to camel logistics, we own the full chain.",
      fr: "Séminaires d'entreprise, incentives, congrès, lancements produits et soirées de gala — mis en scène à travers kasbahs, riads et le désert ouvert. De l'audiovisuel à la logistique chameaux, nous maîtrisons toute la chaîne.",
      es: "Seminarios corporativos, incentivos, congresos, lanzamientos y galas — puestos en escena en kasbahs, riads y el desierto abierto. Del audiovisual a la logística de camellos, dominamos toda la cadena.",
    },
    bullets: [
      { en: "Capacity up to 1,000 guests", fr: "Capacité jusqu'à 1 000 convives", es: "Capacidad hasta 1.000 personas" },
      { en: "In-house F&B, AV, decor, talent", fr: "F&B, audiovisuel, décor, talents internes", es: "F&B, AV, decoración, talento internos" },
      { en: "Dedicated event producer per file", fr: "Producteur événementiel dédié par dossier", es: "Productor dedicado por evento" },
    ],
  },
  {
    icon: FileText,
    title: { en: "Special projects", fr: "Projets spéciaux", es: "Proyectos especiales" },
    pitch: {
      en: "Film, fashion and editorial productions, luxury expeditions, scientific missions, brand activations and full takeovers — handled with discretion and a permit-clearance team who knows the Moroccan administration.",
      fr: "Productions cinéma, mode et éditoriales, expéditions de luxe, missions scientifiques, activations de marque et privatisations — menées avec discrétion et une équipe permis qui connaît l'administration marocaine.",
      es: "Producciones de cine, moda y editoriales, expediciones de lujo, misiones científicas, activaciones de marca y privatizaciones — gestionadas con discreción y un equipo de permisos que conoce la administración marroquí.",
    },
    bullets: [
      { en: "Permitting & location scouting", fr: "Permis & repérages", es: "Permisos y scouting" },
      { en: "Full property privatisations", fr: "Privatisation complète d'adresses", es: "Privatización completa" },
      { en: "Discretion clauses & NDA", fr: "Clauses de confidentialité & NDA", es: "Cláusulas de confidencialidad y NDA" },
    ],
  },
];

const TRUST_STATS = [
  { value: "30+", label: { en: "Years on the ground", fr: "Années sur le terrain", es: "Años sobre el terreno" } },
  { value: "180+", label: { en: "Travel-trade partners worldwide", fr: "Partenaires trade dans le monde", es: "Partners trade en el mundo" } },
  { value: "92%", label: { en: "Agency retention year-on-year", fr: "Rétention agences année après année", es: "Retención agencias año tras año" } },
  { value: "24h", label: { en: "Average proposal turnaround", fr: "Délai moyen de réponse", es: "Tiempo medio de respuesta" } },
];

const TRUST_QUOTES = [
  {
    quote: {
      en: "We have been bringing groups to Grup Xaluca since 2009. In sixteen years not a single emergency has hit us — because their team has solved every one before we even heard about it.",
      fr: "Nous amenons des groupes à Grup Xaluca depuis 2009. En seize ans, pas une urgence ne nous a touchés — parce que leurs équipes ont tout résolu avant même que nous en entendions parler.",
      es: "Llevamos grupos a Grup Xaluca desde 2009. En dieciséis años, ni una sola emergencia nos ha alcanzado — porque su equipo lo ha resuelto antes de que nos enteráramos.",
    },
    name: "Marc Béringer",
    role: { en: "Senior product manager · Voyageurs du Monde", fr: "Senior product manager · Voyageurs du Monde", es: "Senior product manager · Voyageurs du Monde" },
  },
  {
    quote: {
      en: "A thousand-pax gala under a single tent at Erg Chebbi, served plated, on time, in 38°C. Xaluca made the impossible look routine.",
      fr: "Un gala de mille convives sous une seule toile à l'Erg Chebbi, servi à l'assiette, à l'heure, par 38°C. Xaluca a rendu l'impossible routinier.",
      es: "Una gala de mil personas bajo una sola tienda en Erg Chebbi, emplatada, puntual, a 38°C. Xaluca hizo que lo imposible pareciera rutina.",
    },
    name: "Sandra Lichtenfeld",
    role: { en: "Head of MICE · Pacha Events Group", fr: "Head of MICE · Pacha Events Group", es: "Head of MICE · Pacha Events Group" },
  },
  {
    quote: {
      en: "They run the south. Full stop. Hotels, transfers, food, dunes, paperwork — one phone call and everything moves.",
      fr: "Ils tiennent le sud. Point. Hôtels, transferts, restauration, dunes, paperasse — un coup de fil et tout s'enclenche.",
      es: "Ellos dirigen el sur. Punto. Hoteles, traslados, comida, dunas, papeleo — una llamada y todo se pone en marcha.",
    },
    name: "Liam Hartley",
    role: { en: "Founder · Atlas & Argan Bespoke Travel", fr: "Founder · Atlas & Argan Bespoke Travel", es: "Founder · Atlas & Argan Bespoke Travel" },
  },
];

const BUSINESS_TYPES = [
  { en: "Travel agency", fr: "Agence de voyages", es: "Agencia de viajes" },
  { en: "Tour operator", fr: "Tour-opérateur", es: "Turoperador" },
  { en: "DMC", fr: "DMC", es: "DMC" },
  { en: "MICE / Event agency", fr: "Agence MICE / Événements", es: "Agencia MICE / Eventos" },
  { en: "Corporate travel planner", fr: "Voyages d'affaires", es: "Viajes corporativos" },
  { en: "Incentive house", fr: "Incentive house", es: "Incentive house" },
  { en: "Production / Film / Editorial", fr: "Production / Film / Édito", es: "Producción / Cine / Editorial" },
  { en: "Other", fr: "Autre", es: "Otro" },
];

// ── PAGE ──────────────────────────────────────────────────────────────
const PartnersPage = () => {
  const { lang } = useLanguage();
  const [form, setForm] = useState({
    company: "", contact: "", role: "", email: "", phone: "",
    country: "", business_type: "", timing: "", volume: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setField = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact || !form.email) {
      toast.error(pick(COPY.form_error, lang));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/partner-requests`, {
        ...form,
        language: lang,
        source: "partners-page",
      });
      setSubmitted(true);
      toast.success(pick(COPY.form_success, lang));
    } catch (err) {
      // Fall back gracefully — the /partner-requests endpoint is not
      // always deployed; route the enquiry through /contact instead.
      // That endpoint accepts a plain name+email+message shape so we
      // pack the partner-specific fields inside the message body.
      try {
        const partnerMessage = [
          `[PARTNERS] ${form.company} · ${form.business_type || "—"} · ${form.country || "—"}`,
          "",
          form.message || "(no message)",
          "",
          `Role: ${form.role || "—"}`,
          `Volume: ${form.volume || "—"}`,
          `Timing: ${form.timing || "—"}`,
          form.phone ? `Phone: ${form.phone}` : null,
        ]
          .filter((s) => s !== null)
          .join("\n");
        await axios.post(`${API}/contact`, {
          name: form.contact,
          email: form.email,
          subject: `B2B partner enquiry — ${form.company}`,
          message: partnerMessage,
          language: lang,
        });
        setSubmitted(true);
        toast.success(pick(COPY.form_success, lang));
      } catch (err2) {
        toast.error(pick(COPY.form_error, lang));
      }
    }
    setSubmitting(false);
  };

  const scrollToContact = (e) => {
    e?.preventDefault?.();
    document.getElementById("partner-contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="page-partners" className="bg-[#FDFBF7] text-[#2C2621]">

      {/* ───── HERO ───── */}
      <section className="relative min-h-[88vh] flex flex-col justify-end overflow-hidden" data-cms-scope="partners.hero">
        <img
          src={HERO_IMAGE}
          alt={pick({ en: "Grup Xaluca partners", fr: "Partenaires de Grup Xaluca", es: "Socios de Grup Xaluca" }, lang)}
          className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async"
          data-cms-role="bg"/>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/95 via-[#1A1513]/60 to-[#1A1513]/25" />
        <span className="film-grain" aria-hidden="true" />
        <BrandMonogramEdge size="xl" opacity={0.55} testIdPrefix="partners-hero" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28 pt-40 md:pt-48 text-[#FDFBF7] z-[1]">
          <p
            className="text-[10px] tracking-[0.35em] uppercase text-[#D4A373]"
            data-testid="partners-hero-overline"
          >
            {pick(COPY.hero_overline, lang)}
          </p>
          <h1
            className="font-serif-x mt-5 text-5xl md:text-7xl lg:text-[88px] leading-[0.98] tracking-tighter max-w-5xl"
            data-testid="partners-hero-title"
          >
            {pick(COPY.hero_title, lang)}{" "}
            <em className="not-italic text-[#D4A373]">{pick(COPY.hero_title_accent, lang)}</em>
          </h1>
          <p className="mt-7 text-base md:text-xl text-[#FDFBF7]/85 max-w-3xl leading-relaxed">
            {pick(COPY.hero_sub, lang)}
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={scrollToContact}
              className="bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-7 md:px-8 py-4 text-[11px] tracking-[0.25em] uppercase transition-colors inline-flex items-center gap-2"
              data-testid="partners-hero-cta-primary"
            >
              {pick(COPY.hero_cta_primary, lang)}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
            </button>
            <button
              type="button"
              onClick={scrollToContact}
              className="border border-[#FDFBF7]/40 hover:bg-[#FDFBF7] hover:text-[#1A1513] text-[#FDFBF7] px-7 md:px-8 py-4 text-[11px] tracking-[0.25em] uppercase transition-colors"
              data-testid="partners-hero-cta-secondary"
            >
              {pick(COPY.hero_cta_secondary, lang)}
            </button>
            <a
              href="mailto:partners@xaluca.com"
              className="text-[11px] tracking-[0.25em] uppercase text-[#FDFBF7]/80 hover:text-[#D4A373] border-b border-[#FDFBF7]/35 hover:border-[#D4A373] pb-1 transition-colors"
              data-testid="partners-hero-cta-tertiary"
            >
              {pick(COPY.hero_cta_tertiary, lang)}
            </a>
          </div>
        </div>
        {/* Bottom-right brand corner — consistent with /booking, /hotels/* and home */}
        <div className="absolute bottom-6 right-6 hidden md:flex items-center gap-2 text-[#FDFBF7]/70 text-[10px] tracking-[0.3em] uppercase z-10 pointer-events-none" data-testid="partners-hero-corner-label">
          <span className="w-12 h-px bg-[#FDFBF7]/40" />
          GRUP XALUCA
        </div>
      </section>

      {/* ───── STATS RIBBON ───── */}
      <section id="partners-stats" className="bg-[#1A1513] text-[#FDFBF7] scroll-mt-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-14 grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6">
          {COPY.stats.map((s, i) => (
            <div key={`hs-${i}`} className="text-left" data-testid={`partners-hero-stat-${i}`}>
              <p className="font-serif-x text-4xl md:text-5xl text-[#D4A373] tabular-nums leading-none">
                {s.value}
              </p>
              <p className="mt-3 text-[10px] tracking-[0.28em] uppercase text-[#FDFBF7]/70 leading-snug">
                {pick(s.label, lang)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <PageStickyNav
        items={[
          { id: "partners-stats", label: { en: "Numbers", fr: "Chiffres", es: "Cifras" } },
          { id: "partners-about", label: { en: "About", fr: "À propos", es: "Nosotros" } },
          { id: "partners-why", label: { en: "Why us", fr: "Pourquoi", es: "Por qué" } },
          { id: "partners-network", label: { en: "Network", fr: "Réseau", es: "Red" } },
          { id: "partners-solutions", label: { en: "Solutions", fr: "Solutions", es: "Soluciones" } },
          { id: "partners-trust", label: { en: "Trust", fr: "Confiance", es: "Confianza" } },
          { id: "partner-contact", label: { en: "Contact", fr: "Contact", es: "Contacto" } },
        ]}
      />

      {/* ───── ABOUT ───── */}
      <section id="partners-about" className="py-24 md:py-32 bg-[#FDFBF7] scroll-mt-32" data-testid="partners-about" data-cms-scope="partners.about">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="relative aspect-[3/4] overflow-hidden">
              <img src={ABOUT_IMAGE} alt="Grup Xaluca · Arfoud" className="w-full h-full object-cover" loading="lazy" decoding="async" data-cms-role="image"/>
              <div className="absolute bottom-4 left-4 right-4 text-[#FDFBF7] text-[10px] tracking-[0.3em] uppercase flex items-center gap-2">
                <span className="w-10 h-px bg-[#FDFBF7]/50" />
                Arfoud · Sahara gateway
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#C16542]">{pick(COPY.about_overline, lang)}</p>
            <h2 className="font-serif-x mt-3 text-4xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tight text-[#2C2621] max-w-3xl">
              {pick(COPY.about_title, lang)}
            </h2>
            <p className="mt-8 text-base md:text-lg text-[#5C5248] leading-relaxed max-w-2xl">{pick(COPY.about_lead, lang)}</p>
            <p className="mt-5 text-base md:text-lg text-[#5C5248] leading-relaxed max-w-2xl">{pick(COPY.about_p2, lang)}</p>
            <ul className="mt-8 grid sm:grid-cols-2 gap-3" data-testid="partners-about-facts">
              {COPY.about_facts.map((f, i) => (
                <li key={`af-${i}`} className="flex items-start gap-2.5 text-sm text-[#2C2621]">
                  <CheckCircle2 className="w-4 h-4 text-[#C16542] mt-0.5 shrink-0" strokeWidth={1.6} />
                  <span>{pick(f, lang)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ───── WHY PARTNER ───── */}
      <section id="partners-why" className="py-24 md:py-32 bg-[#F2EBE1]/40 border-y border-[#2C2621]/10 scroll-mt-32" data-testid="partners-why">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-14 md:mb-20">
            <div className="lg:col-span-6">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#C16542]">{pick(COPY.why_overline, lang)}</p>
              <h2 className="font-serif-x mt-3 text-4xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tight text-[#2C2621]">
                {pick(COPY.why_title, lang)}
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-3">
              <p className="text-base md:text-lg text-[#5C5248] leading-relaxed max-w-prose">{pick(COPY.why_lead, lang)}</p>
            </div>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12" data-testid="partners-why-pillars">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <li key={`pil-${i}`} className="relative" data-testid={`partners-pillar-${i + 1}`}>
                  <span aria-hidden="true" className="absolute -top-2 right-0 font-serif-x text-[64px] leading-none text-[#D4A373]/12 tabular-nums select-none pointer-events-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="inline-flex items-center justify-center w-12 h-12 mb-5 bg-[#FDF8EE] border border-[#D4A373]/40 text-[#C16542] relative z-[1]">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif-x text-xl md:text-2xl leading-tight text-[#2C2621]">{pick(p.title, lang)}</h3>
                  <p className="mt-3 text-sm md:text-[15px] leading-relaxed text-[#5C5248]">{pick(p.body, lang)}</p>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ───── NETWORK ───── */}
      <section id="partners-network" className="py-24 md:py-32 bg-[#FDFBF7] scroll-mt-32" data-testid="partners-network">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-16 md:mb-20">
            <div className="lg:col-span-6">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#C16542]">{pick(COPY.network_overline, lang)}</p>
              <h2 className="font-serif-x mt-3 text-4xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tight text-[#2C2621]">
                {pick(COPY.network_title, lang)}
              </h2>
              <p className="mt-7 text-base md:text-lg text-[#5C5248] leading-relaxed max-w-prose">{pick(COPY.network_lead, lang)}</p>
            </div>
            <div className="lg:col-span-6" data-cms-scope="partners.network">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={NETWORK_IMAGE} alt={pick({ en: "Grup Xaluca network", fr: "Réseau Grup Xaluca", es: "Red Grup Xaluca" }, lang)} className="w-full h-full object-cover" loading="lazy" decoding="async" data-cms-role="image"/>
              </div>
            </div>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#2C2621]/15" data-testid="partners-network-list">
            {NETWORK.map((n, i) => {
              const Icon = n.icon;
              return (
                <li key={`nw-${i}`} className="bg-[#FDFBF7] p-7 md:p-8 flex flex-col gap-3" data-testid={`partners-network-item-${i + 1}`}>
                  <span className="inline-flex items-center justify-center w-10 h-10 text-[#C16542] border border-[#D4A373]/40 bg-[#FDF8EE]">
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                  </span>
                  <p className="font-serif-x text-lg md:text-xl leading-tight text-[#2C2621]">{pick(n.title, lang)}</p>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-[#A07042]">{pick(n.stat, lang)}</p>
                  <p className="text-sm text-[#5C5248] leading-relaxed">{pick(n.body, lang)}</p>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ───── SOLUTIONS ───── */}
      <section id="partners-solutions" className="py-24 md:py-32 bg-[#1A1513] text-[#FDFBF7] scroll-mt-32" data-testid="partners-solutions">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-16">
            <div className="lg:col-span-6">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4A373]">{pick(COPY.solutions_overline, lang)}</p>
              <h2 className="font-serif-x mt-3 text-4xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tight">
                {pick(COPY.solutions_title, lang)}
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-3">
              <p className="text-base md:text-lg text-[#FDFBF7]/80 leading-relaxed max-w-prose">{pick(COPY.solutions_lead, lang)}</p>
            </div>
          </div>
          <ul className="grid md:grid-cols-2 gap-px bg-[#FDFBF7]/15" data-testid="partners-solutions-list">
            {SOLUTIONS.map((s, i) => {
              const Icon = s.icon;
              return (
                <li key={`sol-${i}`} className="bg-[#1A1513] p-8 md:p-10 flex flex-col gap-4" data-testid={`partners-solution-${i + 1}`}>
                  <span className="inline-flex items-center justify-center w-12 h-12 text-[#D4A373] border border-[#D4A373]/40">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  <h3 className="font-serif-x text-2xl md:text-3xl leading-tight">{pick(s.title, lang)}</h3>
                  <p className="text-sm md:text-[15px] text-[#FDFBF7]/80 leading-relaxed">{pick(s.pitch, lang)}</p>
                  <ul className="mt-2 space-y-2">
                    {s.bullets.map((b, j) => (
                      <li key={`sol-${i}-b-${j}`} className="flex items-start gap-2 text-sm text-[#FDFBF7]/90">
                        <ChevronRight className="w-3.5 h-3.5 text-[#D4A373] mt-1 shrink-0" strokeWidth={1.7} />
                        {pick(b, lang)}
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ───── TRUST ───── */}
      <section id="partners-trust" className="py-24 md:py-32 bg-[#FDFBF7] scroll-mt-32" data-testid="partners-trust">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#C16542]">{pick(COPY.trust_overline, lang)}</p>
            <h2 className="font-serif-x mt-3 text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-[#2C2621]">
              {pick(COPY.trust_title, lang)}
            </h2>
          </div>
          <ul className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-6 mb-16" data-testid="partners-trust-stats">
            {TRUST_STATS.map((s, i) => (
              <li key={`ts-${i}`} className="text-center" data-testid={`partners-trust-stat-${i + 1}`}>
                <p className="font-serif-x text-5xl md:text-6xl text-[#C16542] tabular-nums leading-none">{s.value}</p>
                <p className="mt-3 text-[10px] tracking-[0.28em] uppercase text-[#5C5248] leading-snug">{pick(s.label, lang)}</p>
              </li>
            ))}
          </ul>
          <ul className="grid md:grid-cols-3 gap-6 md:gap-8" data-testid="partners-trust-quotes">
            {TRUST_QUOTES.map((q, i) => (
              <li key={`tq-${i}`} className="bg-[#F2EBE1]/50 border border-[#D4A373]/30 p-7 md:p-8 flex flex-col gap-5" data-testid={`partners-trust-quote-${i + 1}`}>
                <p className="font-serif-x text-lg md:text-xl text-[#2C2621] italic leading-relaxed">
                  &ldquo;{pick(q.quote, lang)}&rdquo;
                </p>
                <div className="mt-auto pt-4 border-t border-[#2C2621]/15">
                  <p className="font-serif-x text-[15px] text-[#2C2621]">{q.name}</p>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-[#A07042] mt-1">{pick(q.role, lang)}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ───── CONTACT / CTA ───── */}
      <section
        id="partner-contact"
        className="relative py-24 md:py-32 bg-[#FDFBF7] border-t border-[#2C2621]/15"
        data-testid="partners-contact"
      >
        <div className="absolute inset-0 pointer-events-none opacity-60" aria-hidden="true" style={{ background: "radial-gradient(ellipse 50% 50% at 50% -10%, rgba(212,163,115,0.18) 0%, transparent 60%)" }} />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#C16542]">{pick(COPY.contact_overline, lang)}</p>
            <h2 className="font-serif-x mt-3 text-4xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tight text-[#2C2621]">
              {pick(COPY.contact_title, lang)}
            </h2>
            <p className="mt-7 text-base md:text-lg text-[#5C5248] leading-relaxed">{pick(COPY.contact_lead, lang)}</p>
          </div>
          {submitted ? (
            <div className="bg-[#FDF8EE] border border-[#D4A373]/50 p-8 md:p-10 text-center max-w-2xl mx-auto" data-testid="partners-contact-success">
              <CheckCircle2 className="w-10 h-10 text-[#C16542] mx-auto" strokeWidth={1.5} />
              <p className="font-serif-x text-2xl md:text-3xl mt-5 text-[#2C2621] leading-tight">
                {pick({ en: "Thank you — we'll be in touch within 24 hours.", fr: "Merci — nous vous répondons sous 24 heures.", es: "Gracias — le respondemos en 24 horas." }, lang)}
              </p>
              <p className="mt-4 text-sm text-[#5C5248]">{pick(COPY.form_footnote, lang)}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto" data-testid="partners-contact-form">
              <div className="md:col-span-2 grid sm:grid-cols-2 gap-5">
                <FormField label={pick(COPY.form_company, lang)} testid="partner-company" required>
                  <input type="text" required value={form.company} onChange={setField("company")} className={inputCls} data-testid="partner-input-company" />
                </FormField>
                <FormField label={pick(COPY.form_contact, lang)} testid="partner-contact-name" required>
                  <input type="text" required value={form.contact} onChange={setField("contact")} className={inputCls} data-testid="partner-input-contact" />
                </FormField>
              </div>
              <FormField label={pick(COPY.form_role, lang)} testid="partner-role">
                <input type="text" value={form.role} onChange={setField("role")} className={inputCls} data-testid="partner-input-role" />
              </FormField>
              <FormField label={pick(COPY.form_country, lang)} testid="partner-country">
                <input type="text" value={form.country} onChange={setField("country")} className={inputCls} data-testid="partner-input-country" />
              </FormField>
              <FormField label={pick(COPY.form_email, lang)} testid="partner-email" required>
                <input type="email" required value={form.email} onChange={setField("email")} className={inputCls} data-testid="partner-input-email" />
              </FormField>
              <FormField label={pick(COPY.form_phone, lang)} testid="partner-phone">
                <input type="tel" value={form.phone} onChange={setField("phone")} className={inputCls} data-testid="partner-input-phone" />
              </FormField>
              <FormField label={pick(COPY.form_type, lang)} testid="partner-type">
                <select value={form.business_type} onChange={setField("business_type")} className={inputCls} data-testid="partner-input-type">
                  <option value="">—</option>
                  {BUSINESS_TYPES.map((t) => (
                    <option key={t.en} value={t.en}>{pick(t, lang)}</option>
                  ))}
                </select>
              </FormField>
              <FormField label={pick(COPY.form_timing, lang)} testid="partner-timing">
                <input type="text" value={form.timing} onChange={setField("timing")} placeholder="e.g. Spring 2026 — 14 nights" className={inputCls} data-testid="partner-input-timing" />
              </FormField>
              <div className="md:col-span-2">
                <FormField label={pick(COPY.form_volume, lang)} testid="partner-volume">
                  <input type="text" value={form.volume} onChange={setField("volume")} placeholder="e.g. 4 series x 24 pax · 800 room-nights" className={inputCls} data-testid="partner-input-volume" />
                </FormField>
              </div>
              <div className="md:col-span-2">
                <FormField label={pick(COPY.form_message, lang)} testid="partner-message">
                  <textarea rows={6} value={form.message} onChange={setField("message")} placeholder={pick(COPY.form_message_ph, lang)} className={`${inputCls} resize-y`} data-testid="partner-input-message" />
                </FormField>
              </div>
              <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 pt-3">
                <p className="text-[11px] text-[#5C5248] leading-relaxed max-w-md">{pick(COPY.form_footnote, lang)}</p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-3 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] disabled:opacity-60 px-8 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors whitespace-nowrap"
                  data-testid="partners-contact-submit"
                >
                  {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" strokeWidth={1.6} />}
                  {submitting ? pick(COPY.form_sending, lang) : pick(COPY.form_submit, lang)}
                </button>
              </div>
            </form>
          )}
          <div className="mt-16 pt-10 border-t border-[#2C2621]/15 grid md:grid-cols-3 gap-6 text-sm text-[#2C2621]">
            <ContactLine icon={Mail} label={pick({ en: "B2B desk", fr: "Bureau B2B", es: "Mesa B2B" }, lang)} value="partners@xaluca.com" href="mailto:partners@xaluca.com" />
            <ContactLine icon={Phone} label={pick({ en: "Reservations · Arfoud HQ", fr: "Réservations · QG Arfoud", es: "Reservas · QG Arfoud" }, lang)} value="+212 535 57 60 22" href="tel:+212535576022" />
            <ContactLine icon={Calendar} label={pick({ en: "Book a Zoom with the team", fr: "Réserver un Zoom avec l'équipe", es: "Reservar un Zoom con el equipo" }, lang)} value={pick({ en: "EN · FR · ES — within 24h", fr: "EN · FR · ES — sous 24h", es: "EN · FR · ES — en 24h" }, lang)} href="mailto:partners@xaluca.com?subject=Zoom%20with%20Grup%20Xaluca%20B2B" />
          </div>
        </div>
      </section>
    </div>
  );
};

const inputCls = "w-full bg-white border border-[#2C2621]/15 hover:border-[#C16542] focus:border-[#C16542] outline-none px-3.5 py-3 text-sm text-[#2C2621] transition-colors";

const FormField = ({ label, children, required, testid }) => (
  <label className="block" data-testid={`partner-field-${testid}`}>
    <span className="block text-[10px] tracking-[0.22em] uppercase text-[#5C5248] mb-1.5">
      {label}{required ? <span className="text-[#C16542] ml-1">*</span> : null}
    </span>
    {children}
  </label>
);

const ContactLine = ({ icon: Icon, label, value, href }) => (
  <a href={href} className="flex items-start gap-3 group">
    <span className="inline-flex items-center justify-center w-9 h-9 shrink-0 border border-[#2C2621]/15 group-hover:border-[#C16542] text-[#5C5248] group-hover:text-[#C16542] transition-colors">
      <Icon className="w-4 h-4" strokeWidth={1.6} />
    </span>
    <span className="flex flex-col leading-tight">
      <span className="text-[10px] tracking-[0.25em] uppercase text-[#A07042]">{label}</span>
      <span className="mt-1 font-serif-x text-[15px] text-[#2C2621] group-hover:text-[#C16542] transition-colors">{value}</span>
    </span>
  </a>
);

export default PartnersPage;
